import _ from 'lodash'
import React from 'react'
import { Entity } from 'shared-libs/models/entity'
import { ConfirmationModal } from 'browser/components/atomic-elements/organisms/confirmation-modal'

export async function saveWithWarnings(entity: Entity, saveArgs?: any): Promise<Entity> {
  // WARNING - do not call this function with long-lived args object, function is destructive
  saveArgs = saveArgs ? [...saveArgs] : []
  saveArgs[0] ||= {}
  saveArgs[0].errorSeverityThreshold = 'warning'

  try {
    return await entity.save(...saveArgs)
  } catch (errorResponse) {
    if (!isApiValidationError(errorResponse)) {
      throw errorResponse
    }
    const { errors, errorSeverities } = errorResponse

    const warningsMap = _.mapValues<typeof errors, string[]>(errors, (messages: string[], key: string) =>
      _.filter(messages, (_msg, index) => errorSeverities?.[key]?.[index] === 'warning')
    )

    const warningCount = _.sumBy(_.values(warningsMap), _.size)

    if (warningCount === 0) {
      throw errorResponse
    }

    return new Promise((resolve, reject) => {
      ConfirmationModal.open({
        modalDialogClassName: 'c-modal-dialog--md',
        confirmationTitle: 'Warning',
        confirmationText: renderWarningBody(entity, warningsMap),
        primaryButtonText: 'Save Anyway',
        cancelButtonText: 'Cancel',
        onPrimaryClicked: () => {
          saveArgs[0].errorSeverityThreshold = 'error'
          entity.save(...saveArgs).then(resolve).catch(reject)
        },
        onCancelCallback: () => {
          reject(errorResponse)
        },
      })
    })
  }
}

function isApiValidationError(e: unknown): e is { errors: Record<string, string[]>; errorSeverities?: Record<string, string[]> } {
  return typeof e === 'object' && e !== null && 'errors' in e
}

function renderWarningBody(entity: Entity, warningsMap: Record<string, string[]>): React.ReactNode {
  const labeled = _.mapKeys(warningsMap, (_val, key) => {
    const path = key.split('.')
    const schemaEntity = entity.resolveSubschemaByValuePath(path)
    return schemaEntity?.schema?.label || key
  })
  const joined = _.mapValues(labeled, (warnings) => _.join(warnings, ', '))

  const isSingle = _.size(joined) === 1

  const Container = ({ children }: { children: React.ReactNode }) =>
    isSingle ? <div>{children}</div> : <ul className="dib tl">{children}</ul>
  const Item = ({ children }: { children: React.ReactNode }) =>
    isSingle ? <div>{children}</div> : <li>{children}</li>

  return (
    <>
      <div className="u-bumperBottom">
        There was a problem saving:
      </div>
      <Container>
        {_.map(joined, (message, key) => (
          <Item key={key}>
            <strong>{key}</strong>: {message}
          </Item>
        ))}
      </Container>
    </>
  )
}
